import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ANALYTICS_EVENTS,
  AXIS_IDS,
  COND_IDS,
  band,
  buildProfile,
  getContent,
  type Answers,
} from '@neke/core';
import type { ApiError } from '../api/client';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { AxisRow } from '../components/AxisRow';
import { track } from '../analytics';

export function Result() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, locale, ready } = useSession();
  const content = getContent(locale);

  const [answers, setAnswers] = useState<Answers | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteCount, setInviteCount] = useState<number | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate('/login?next=/result');
      return;
    }
    void api
      .latestAssessment()
      .then((a) => {
        setAnswers(a.answers);
        void track(ANALYTICS_EVENTS.resultViewed, { status: a.status });
      })
      .catch((e: ApiError) => {
        // Ветки на `consent_required` здесь намеренно нет: чтение своих данных
        // сервер согласием не закрывает (гейт висит только на трёх маршрутах,
        // которые ответы принимают, — см. Test.tsx). Иначе клиенту было бы не
        // на чем решать, вести ли человека в онбординг.
        setError(e.status === 404 ? 'empty' : e.message);
        // Пустой результат — своя ступень воронки: человек дошёл, а анкеты нет.
        if (e.status === 404) void track(ANALYTICS_EVENTS.resultEmpty);
      });
    // Счётчик — украшение ссылки на историю: не вышло, показываем ссылку без него.
    void api
      .myInvites()
      .then((list) => setInviteCount(list.length))
      .catch(() => setInviteCount(null));
  }, [ready, user, navigate]);

  if (error === 'empty') {
    return (
      <div className="page">
        <div className="card">
          <p>{t('result.empty')}</p>
          <Link to="/test" className="btn btn-primary">
            {t('test.start')}
          </Link>
        </div>
      </div>
    );
  }

  if (error) return <div className="page"><p className="muted">{error}</p></div>;
  if (!answers) return <div className="page"><p className="muted">{t('common.loading')}</p></div>;

  const profile = buildProfile(answers);

  const byLevel = (level: number) =>
    COND_IDS.filter((_, i) => profile.condLevels[i] === level);

  const createInvite = () => {
    setInviteBusy(true);
    setInviteError(null);
    void api
      .createInvite(recipient.trim())
      .then((data) => {
        setInvite(data.url);
        // Поле чистим сразу: следующего зовут с этого же экрана.
        setRecipient('');
        // Ссылка новая — «Скопировано» от предыдущей на ней висеть не должно.
        setCopied(false);
        setInviteCount((n) => (n === null ? null : n + 1));
        // Ни ссылку, ни имя получателя в событие не кладём: ссылка открывает
        // чужую анкету, а имя — приватная пометка автора.
        void track(ANALYTICS_EVENTS.inviteCreated);
      })
      .catch((e: ApiError) => setInviteError(e.message))
      .finally(() => setInviteBusy(false));
  };

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('result.title')}</h1>
        <p className="muted">{t('result.lead')}</p>
      </div>

      <div className="card">
        {AXIS_IDS.map((axis) => (
          <AxisRow
            key={axis}
            content={content.axes[axis]}
            value={profile.axes[axis]}
            note={content.axes[axis][band(profile.axes[axis])]}
          />
        ))}
      </div>

      <div className="stack">
        <h2>{t('result.conditions')}</h2>
        <p className="fine">{t('result.conditionsLead')}</p>
        {[1, 2, 3].map((level) => (
          <details className="group" key={level} open={level === 1}>
            <summary>
              {content.levels[level - 1]}
              <span className="fine count">
                {byLevel(level).length}
              </span>
            </summary>
            <div className="body">
              <p className="fine">{content.levelHints[level - 1]}</p>
              {byLevel(level).map((cond) => (
                <span className="chip" key={cond}>
                  {content.conditions[cond].title}
                </span>
              ))}
            </div>
          </details>
        ))}
      </div>

      <div className="card stack">
        <h2>{t('result.invite')}</h2>
        <p className="fine">{t('result.inviteLead')}</p>

        <label className="field">
          <span>{t('result.recipientName')}</span>
          <input
            type="text"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </label>
        <p className="fine">{t('result.recipientHint')}</p>

        <button
          className="btn btn-primary"
          onClick={createInvite}
          disabled={inviteBusy || recipient.trim() === ''}
        >
          {inviteBusy ? t('common.loading') : t('result.inviteCreate')}
        </button>

        {inviteError && (
          <p className="fine error">
            {inviteError}
          </p>
        )}

        {invite && (
          <>
            <p className="chip chip-code">{invite}</p>
            <button
              className="btn btn-gold"
              onClick={() => {
                void navigator.clipboard.writeText(invite).then(() => {
                  setCopied(true);
                  void track(ANALYTICS_EVENTS.inviteLinkCopied);
                  setTimeout(() => setCopied(false), 1800);
                });
              }}
            >
              {copied ? t('result.copied') : t('result.copyLink')}
            </button>
          </>
        )}

        <Link to="/invites" className="btn btn-ghost">
          {inviteCount === null
            ? t('result.inviteHistory')
            : t('result.inviteHistoryCount', { count: inviteCount })}
        </Link>
      </div>
    </div>
  );
}
